import { TierDisplay } from '@components/ui/analysis-config/AnalysisModeSelector';
import { hasAccess } from '@parlay-picker/shared/featureAccess';
import type { SubscriptionTier } from '@parlay-picker/shared/subscriptionTier';
import { lotteryWarning, reachesLotteryTier } from '@parlay-picker/shared/tierInputPolicy';
import type { AnalysisConfig } from '@views/picks/constants';
import type { ReactElement } from 'react';
import { Section } from './Section';

interface ParlayStrategySectionProps {
  cfg: AnalysisConfig;
  isExpanded: boolean;
  onToggleExpand: (sectionKey: string) => void;
  setConfig: React.Dispatch<React.SetStateAction<AnalysisConfig>>;
  updateConfig: (section: string, key: string, value: boolean | number | string) => void;
  tier: SubscriptionTier;
}

export function ParlayStrategySection({
  cfg,
  isExpanded,
  onToggleExpand,
  setConfig,
  updateConfig,
  tier,
}: ParlayStrategySectionProps): ReactElement {
  const lotteryLocked = !hasAccess(tier, 'lottery_ticket_tier');
  return (
    <Section
      title="Parlay Strategy"
      sectionKey="parlayStrategy"
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      enabledCount={0}
      totalCount={0}
      showCount={false}
    >
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white">Leg Count Range</span>
            <span className="text-xs text-purple-400">
              {cfg.parlayStrategy.minLegCount}-{cfg.parlayStrategy.maxLegCount} legs
            </span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={cfg.parlayStrategy.minLegCount}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  parlayStrategy: {
                    ...prev.parlayStrategy,
                    minLegCount: parseInt(e.target.value, 10),
                  },
                }))
              }
              className="flex-1 bg-theme-surface-hover text-gray-200 text-sm rounded px-3 py-1.5 border border-theme-border focus:border-purple-500 focus:outline-none"
            >
              <option value={1}>1 min</option>
              <option value={2}>2 min</option>
              <option value={3}>3 min</option>
              <option value={4}>4 min</option>
            </select>
            <span className="text-theme-text-tertiary">to</span>
            <select
              value={cfg.parlayStrategy.maxLegCount}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  parlayStrategy: {
                    ...prev.parlayStrategy,
                    maxLegCount: parseInt(e.target.value, 10),
                  },
                }))
              }
              className="flex-1 bg-theme-surface-hover text-gray-200 text-sm rounded px-3 py-1.5 border border-theme-border focus:border-purple-500 focus:outline-none"
            >
              <option value={2}>2 max</option>
              <option value={3}>3 max</option>
              <option value={4}>4 max</option>
              <option value={5}>5 max</option>
              <option value={6}>6 max</option>
              <option value={7}>7 max</option>
              <option value={8}>8 max</option>
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white">Target Odds Range</span>
            <span className="text-xs text-purple-400">
              +{cfg.parlayStrategy.targetOddsMin} to +{cfg.parlayStrategy.targetOddsMax}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={cfg.parlayStrategy.targetOddsMin}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  parlayStrategy: {
                    ...prev.parlayStrategy,
                    targetOddsMin: parseInt(e.target.value, 10),
                  },
                }))
              }
              className="flex-1 bg-theme-surface-hover text-gray-200 text-sm rounded px-3 py-1.5 border border-theme-border focus:border-purple-500 focus:outline-none"
            >
              <option value={100}>+100</option>
              <option value={150}>+150</option>
              <option value={200}>+200</option>
              <option value={250}>+250</option>
              <option value={300}>+300</option>
            </select>
            <span className="text-theme-text-tertiary">to</span>
            <select
              value={cfg.parlayStrategy.targetOddsMax}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  parlayStrategy: {
                    ...prev.parlayStrategy,
                    targetOddsMax: parseInt(e.target.value, 10),
                  },
                }))
              }
              className="flex-1 bg-theme-surface-hover text-gray-200 text-sm rounded px-3 py-1.5 border border-theme-border focus:border-purple-500 focus:outline-none"
            >
              <option value={250}>+250</option>
              <option value={350}>+350</option>
              <option value={500}>+500</option>
              <option value={750}>+750</option>
              <option value={1000}>+1000</option>
              <option value={1500}>+1500</option>
              <option value={2000}>+2000</option>
            </select>
          </div>
        </div>

        {/* Direction preference */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-theme-text-secondary">Direction Focus</span>
            <span className="text-xs text-theme-text-tertiary">
              {cfg.parlayStrategy.directionPreference === 'over'
                ? 'Hunting Overs'
                : cfg.parlayStrategy.directionPreference === 'under'
                  ? 'Hunting Unders'
                  : 'AI decides'}
            </span>
          </div>
          <div className="flex gap-1 bg-theme-surface rounded-lg p-1">
            {(
              [
                {
                  value: 'balanced' as const,
                  label: 'Balanced',
                  desc: 'Let the AI decide direction based on data',
                },
                {
                  value: 'over' as const,
                  label: 'Overs',
                  desc: 'Focus on finding Over opportunities',
                },
                {
                  value: 'under' as const,
                  label: 'Unders',
                  desc: 'Focus on finding Under opportunities',
                },
              ] as const
            ).map((opt) => {
              const current = cfg.parlayStrategy.directionPreference;
              const isActive = current === opt.value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => updateConfig('parlayStrategy', 'directionPreference', opt.value)}
                  className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition ${
                    isActive
                      ? 'bg-purple-600 text-white'
                      : 'text-theme-text-secondary hover:text-gray-200 hover:bg-theme-surface-hover'
                  }`}
                  title={opt.desc}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Auto-derived tier display */}
        <TierDisplay
          minLegs={cfg.parlayStrategy.minLegCount}
          maxLegs={cfg.parlayStrategy.maxLegCount}
          minOdds={cfg.parlayStrategy.targetOddsMin}
          maxOdds={cfg.parlayStrategy.targetOddsMax}
          lotteryLocked={lotteryLocked}
        />
        {lotteryLocked && reachesLotteryTier(cfg.parlayStrategy) && (
          <p data-testid="lottery-locked-warning" className="text-xs text-theme-warning/80">
            {lotteryWarning()}
          </p>
        )}
      </div>
    </Section>
  );
}
